import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import Reveal, { Eyebrow } from "../components/Reveal";
import StatsBar from "../components/StatsBar";
import LeadForm from "../components/LeadForm";
import Icon from "../components/Icon";

const CREDENTIALS = [
  { icon: "Award", title: "ICAI Chartered Accountants", desc: "Partner-led CA practice covering audit, tax and corporate finance — every report signed by a member in good standing." },
  { icon: "ShieldCheck", title: "ICSI Company Secretaries", desc: "A dedicated secretarial desk running ROC calendars, board governance and NCLT matters for 800+ companies." },
  { icon: "Scale", title: "Bar Council Advocates", desc: "15+ legal advisors across corporate law, contracts, arbitration and IP — practitioners, not form-fillers." },
  { icon: "BadgeCheck", title: "IBBI Registered Valuers", desc: "Registered valuer reports valid under the Companies Act, FEMA and income tax — accepted by funds, banks and regulators." },
];

const VALUES = [
  { num: "01", title: "Deadlines are our problem", body: "You should never know a compliance deadline exists. Our calendar, our reminders, our penalty if we miss." },
  { num: "02", title: "Advice before paperwork", body: "Anyone can file a form. We tell you whether the transaction behind the form is structured right — before it happens." },
  { num: "03", title: "Investor-grade, always", body: "Every register, return and report is built to survive due diligence. Because one day, it will face some." },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Us — 40+ CA/CS Professionals, 15+ Legal Advisors | Complisure Counsultancy"
        description="Meet the team behind 800+ clients and ₹550+ Cr in valuations — ICAI members, ICSI company secretaries, IBBI registered valuers and corporate lawyers."
        canonical="https://compliance-capital-1.preview.emergentagent.com/about"
        jsonLd={[orgJsonLd, breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]}
      />
      <section className="relative overflow-hidden pb-20 pt-36">
        <div className="blob blob-float left-[-8%] top-[-8%] h-96 w-96 bg-brand-blue/10" />
        <div className="dot-grid absolute inset-0 opacity-[0.1]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <Eyebrow>About Complisure</Eyebrow>
            <h1 className="mt-3 max-w-3xl font-display text-4xl font-extrabold tracking-[-0.02em] text-brand-navy sm:text-5xl">
              A CA firm built like the startups <span className="text-gradient">we serve</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-brand-gray sm:text-lg">
              Complisure Counsultancy started with a frustration: founders choosing between slow, government-office-style CA firms and expensive Big-4 retainers. We built the third option — institutional-quality compliance, valuation and legal work, delivered at startup speed.
            </p>
          </Reveal>

          <Reveal delay={0.15} className="mt-10">
            <div className="glass card-lift overflow-hidden rounded-[28px] p-2.5">
              <img
                src="https://images.unsplash.com/photo-1716703742287-2b06c3c6d81a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBmaW5hbmNpYWwlMjBhZHZpc29yJTIwY29ycG9yYXRlJTIwYnVpbGRpbmclMjBzdGFydHVwJTIwZm91bmRlcnN8ZW58MHx8fHwxNzg4Nzg2MTEwfDA&ixlib=rb-4.1.0&q=85"
                alt="Complisure Counsultancy advisory team"
                className="h-80 w-full rounded-[20px] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <StatsBar />

      <section className="bg-brand-ice/50 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal className="mb-12 max-w-3xl">
            <Eyebrow>Credentials</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl">Qualified where it counts</h2>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2">
            {CREDENTIALS.map((c, i) => (
              <Reveal key={c.title} delay={(i % 2) * 0.1}>
                <div data-testid={`credential-${c.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`} className="glass card-lift h-full rounded-3xl p-8">
                  <Icon name={c.icon} className="mb-4 h-7 w-7 text-brand-blue" />
                  <h3 className="font-display text-xl font-bold tracking-tight text-brand-navy">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-gray">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal className="mb-12 max-w-3xl">
            <Eyebrow>Operating Principles</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl">How we work</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.num} delay={i * 0.1}>
                <div className="glass card-lift group h-full rounded-3xl p-8">
                  <span className="font-display text-5xl font-extrabold text-brand-blue/10 transition-colors group-hover:text-brand-blue/25">{v.num}</span>
                  <h3 className="mt-2 font-display text-xl font-bold tracking-tight text-brand-navy">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-gray">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-20 grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <h2 className="font-display text-3xl font-bold tracking-tight text-brand-navy">Meet your future advisory team</h2>
              <p className="mt-4 text-base leading-relaxed text-brand-gray">
                A free 30-minute consultation with a senior CA or CS — not a sales executive. Bring your messiest compliance question; leave with a plan.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <LeadForm sourcePage="/about" />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
